/**
 * /app/$tenantSlug/me/details — Finish Your Details (Ask for Details).
 *
 * HR asked the signed-in person for details or documents. Each item opens
 * its own screen (the same step forms a joiner uses), and what they send
 * goes to HR to check. Nothing reaches the record until HR verifies it.
 * "Already on Record" lists what HR already has; changing a stored value
 * goes through Change Requests.
 */

import { Link, createFileRoute } from "@tanstack/react-router";
import { ChevronRight, IdCard } from "lucide-react";
import { toApiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { ITEM_STATUS_LABEL, itemStatusTone, reasonLabel, sentText } from "@/lib/detail-requests";
import { useMyDetailRequests } from "@/hooks/use-detail-requests";
import { useTenantSlug } from "@/lib/use-tenant-slug";
import { InboxPage } from "@/components/inbox/inbox-page";
import { EmptyState } from "@/components/shared/empty-state";
import { StatusBadge } from "@/components/shared/status-badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/me/details/")({
  component: MyDetailsRoute,
});

function MyDetailsRoute() {
  const tenantSlug = useTenantSlug();
  const q = useMyDetailRequests();
  const open = (q.data?.requests ?? []).filter((r) => r.status === "open");
  const onRecord = q.data?.on_record ?? [];

  return (
    <InboxPage title="Finish Your Details" description="HR asked for a few details or documents. Send each one from here.">
      {q.isLoading && (
        <div className="max-w-2xl space-y-3" aria-busy="true">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}

      {q.isError && (
        <Alert variant="destructive" className="max-w-2xl">
          <AlertTitle>Could not load your requests</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {q.data && open.length === 0 && (
        <EmptyState
          icon={IdCard}
          title="Nothing to send right now"
          description="When HR asks for details or documents, they show up here."
          className="max-w-2xl"
        />
      )}

      <div className="max-w-2xl space-y-4">
        {open.map((r, idx) => {
          const next = r.items.find((i) => i.status === "pending" || i.status === "needs_again");
          return (
            <Card key={r.id} data-testid="my-details-request">
              <CardHeader className="space-y-1 pb-2">
                <CardTitle className="text-base">{reasonLabel(r.reason)}</CardTitle>
                <CardDescription>
                  {r.requested_by_name ? `${r.requested_by_name} asked · ` : ""}due {formatDate(r.due_date)} · {sentText(r)}
                </CardDescription>
                {r.note && <p className="text-sm text-muted-foreground">{r.note}</p>}
              </CardHeader>
              <CardContent>
                <ul className="divide-y">
                  {r.items.map((i) => (
                    <li key={i.id}>
                      <Link
                        to="/app/$tenantSlug/me/details/$itemId"
                        params={{ tenantSlug, itemId: String(i.id) }}
                        className="flex min-h-11 items-center justify-between gap-3 py-2 text-sm hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <span className="min-w-0 font-medium">{i.title}</span>
                        <span className="flex shrink-0 items-center gap-2">
                          <StatusBadge tone={itemStatusTone(i.status)} label={ITEM_STATUS_LABEL[i.status]} />
                          <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
                        </span>
                      </Link>
                      {i.status === "needs_again" && i.ask_again_note && (
                        <p className="pb-2 text-xs text-muted-foreground">HR says: {i.ask_again_note}</p>
                      )}
                    </li>
                  ))}
                </ul>
                {next && idx === 0 && (
                  <Button asChild className="mt-3 min-h-11">
                    <Link
                      to="/app/$tenantSlug/me/details/$itemId"
                      params={{ tenantSlug, itemId: String(next.id) }}
                    >
                      Continue
                    </Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}

        {onRecord.length > 0 && (
          <Card data-testid="my-details-on-record">
            <CardHeader className="space-y-1 pb-2">
              <CardTitle className="text-base">Already on Record</CardTitle>
              <CardDescription>
                HR already has these. To change one, send a{" "}
                <Link
                  to="/app/$tenantSlug/change-requests"
                  params={{ tenantSlug }}
                  className="inline-flex min-h-11 items-center font-medium text-primary underline-offset-4 hover:underline"
                >
                  change request
                </Link>
                .
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="divide-y">
                {onRecord.map((o) => (
                  <li key={`${o.kind}:${o.doc_type ?? ""}`} className="flex min-h-11 items-center justify-between gap-3 py-2 text-sm">
                    <span className="font-medium">{o.title}</span>
                    <span className="break-words text-right text-muted-foreground">{o.masked ?? "On record"}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </InboxPage>
  );
}
